import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field } from '../../components/ui/Field'
import { Select } from '../../components/ui/Select'
import { COUNTRIES } from '../../lib/countries'
import { PROVINCE_OPTIONS } from '../../lib/provinces'
import { cn } from '../../lib/cn'

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }))
const countryName = (code?: string) => COUNTRY_OPTIONS.find((o) => o.value === (code || 'IT'))?.label || code || 'IT'

const looksLikeEmail = (s?: string) => !!s && s.includes('@')
const cleanName = (s?: string) => (looksLikeEmail(s) ? '' : (s ?? '')) // some accounts store the email as the name

const CF_RE = /^[A-Za-z0-9]{16}$/
const PIVA_RE = /^[0-9]{11}$/
const SDI_RE = /^[A-Za-z0-9]{7}$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Billing fields used by both the profile and the /info step. Always shown, always required —
 *  there is no on/off toggle; one of the two tabs (Privato / Azienda) is always active. */
export type BillingValue = {
  billingType?: string // "privato" | "azienda"
  name?: string
  lastname?: string
  codiceFiscale?: string
  businessName?: string
  businessVatId?: string
  businessSDI?: string
  businessPec?: string
  businessAddress?: string
  businessHouseNumber?: string
  businessZipCode?: string
  businessCity?: string
  businessRegionProvince?: string
  businessCountry?: string
  businessEmail?: string
  phoneNumber?: string
  referente?: string
}

export const billingTypeOf = (v: BillingValue) => (v.billingType === 'azienda' ? 'azienda' : 'privato')
/** The Italian-specific identifiers (Codice Fiscale, Partita IVA, Codice Destinatario/SDI) only
 *  apply when the billing country is Italy. For any other country they're optional / free-form,
 *  and the Codice Destinatario becomes "XXXXXXX" (set on the backend). */
export const isItalyBilling = (v: BillingValue) => (v.businessCountry || 'IT') === 'IT'

// EU member states (ISO alpha-2). Used only to tailor the company P.IVA guidance: an EU VAT needs the
// country prefix (e.g. FR123456789), an extra-EU one uses the placeholder OO99999999999. Italy is
// handled separately (isItalyBilling). Everything else (Codice Fiscale, SDI) is identical for all
// foreign countries, EU or not.
const EU_CODES = new Set(['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'])
export const isEuBilling = (v: BillingValue) => EU_CODES.has((v.businessCountry || 'IT').toUpperCase())

/** Required-field errors for the ACTIVE tab — used to gate save / proceed.
 *  Italy: strict CF / P.IVA / SDI. Foreign (non-IT): those are relaxed; only the universal fields
 *  (name / ragione sociale, address, email) stay required. */
export function billingErrors(v: BillingValue): Record<string, boolean> {
  const it = isItalyBilling(v)
  if (billingTypeOf(v) === 'privato') {
    return {
      // Codice Fiscale is required (16 chars) ONLY for Italy. Foreign private clients must leave it
      // empty (FatturaPA) → the field is hidden and never blocks submission.
      ...(it ? { codiceFiscale: !CF_RE.test((v.codiceFiscale ?? '').trim()) } : {}),
      businessAddress: !v.businessAddress?.trim(),
      businessZipCode: !v.businessZipCode?.trim(),
      businessCity: !v.businessCity?.trim(),
      // Province (FatturaPA sigla) is required only for Italy; foreign region is optional.
      ...(it ? { businessRegionProvince: !v.businessRegionProvince?.trim() } : {}),
    }
  }
  const eu = isEuBilling(v)
  return {
    businessName: !v.businessName?.trim(),
    // CF required only for Italy; a company abroad identifies via its VAT/P.IVA, not CF (hidden).
    ...(it ? { codiceFiscale: !CF_RE.test((v.codiceFiscale ?? '').trim()) } : {}),
    // P.IVA: Italy → 11 digits; EU → real VAT with country prefix (non-empty); extra-EU → hidden &
    // fixed to OO99999999999 on the backend, so not required in the form.
    ...(it
      ? { businessVatId: !PIVA_RE.test((v.businessVatId ?? '').trim()) }
      : eu
        ? { businessVatId: !v.businessVatId?.trim() }
        : {}),
    businessAddress: !v.businessAddress?.trim(),
    businessZipCode: !v.businessZipCode?.trim(),
    businessCity: !v.businessCity?.trim(),
    ...(it ? { businessRegionProvince: !v.businessRegionProvince?.trim() } : {}),
    // SDI required only for Italy (7 chars); foreign → hidden, backend auto-sets XXXXXXX.
    ...(it ? { businessSDI: !SDI_RE.test((v.businessSDI ?? '').trim()) } : {}),
    businessEmail: !EMAIL_RE.test((v.businessEmail ?? '').trim()),
  }
}
export const billingValid = (v: BillingValue) => !Object.values(billingErrors(v)).some(Boolean)

export function BillingFields({
  value,
  set,
  touched = false,
  requiredLabel = 'Campo obbligatorio',
}: {
  value: BillingValue
  set: (field: keyof BillingValue, v: string) => void
  touched?: boolean
  requiredLabel?: string
}) {
  const { t } = useTranslation()
  const type = billingTypeOf(value)
  const it = isItalyBilling(value) // Italian billing → strict CF/P.IVA/SDI; foreign → CF/SDI hidden
  const eu = isEuBilling(value)    // EU (non-IT) → P.IVA with country prefix; extra-EU → OO99999999999
  // Foreign clients must have an EMPTY Codice Fiscale (FatturaPA). If one was typed for Italy and the
  // country is then switched abroad, drop the stale value so it isn't sent/saved on a foreign invoice.
  useEffect(() => {
    if (!it && (value.codiceFiscale ?? '').trim() !== '') set('codiceFiscale', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [it])
  const err = billingErrors(value)
  const showErr = (k: string) => (touched && err[k] ? requiredLabel : undefined)
  const Tab = ({ id, label }: { id: 'privato' | 'azienda'; label: string }) => (
    <button
      type="button"
      onClick={() => set('billingType', id)}
      className={cn(
        'flex-1 rounded-lg px-4 py-2.5 text-[13px] font-bold transition sm:flex-none',
        type === id ? 'bg-brand-600 text-white shadow-soft' : 'text-ink-500 hover:text-ink-900',
      )}
    >
      {label}
    </button>
  )

  const addressFields = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
      <Field className="sm:col-span-4" label={t('billing.address')} required value={value.businessAddress ?? ''} onChange={(e) => set('businessAddress', e.target.value)} error={showErr('businessAddress')} />
      <Field className="sm:col-span-2" label={t('billing.houseNumber')} value={value.businessHouseNumber ?? ''} onChange={(e) => set('businessHouseNumber', e.target.value)} />
      <Field className="sm:col-span-2" label={t('billing.zip')} required value={value.businessZipCode ?? ''} onChange={(e) => set('businessZipCode', e.target.value)} error={showErr('businessZipCode')} />
      <Field className="sm:col-span-2" label={t('billing.city')} required value={value.businessCity ?? ''} onChange={(e) => set('businessCity', e.target.value)} error={showErr('businessCity')} />
      {it ? (
        // Italy: pick from the official province list (stores the 2-letter FatturaPA code)
        <div className="sm:col-span-2">
          <label className="gsm-label">{t('billing.province')}<span className="ml-0.5 text-brand-600" aria-hidden="true">*</span></label>
          <Select
            value={value.businessRegionProvince ?? ''}
            options={PROVINCE_OPTIONS}
            onChange={(v) => set('businessRegionProvince', v)}
            searchable
            ariaLabel={t('billing.province')}
            placeholder={t('billing.province')}
            searchPlaceholder={t('billing.provinceSearch')}
          />
          {showErr('businessRegionProvince') && <p className="mt-1 text-xs font-medium text-brand-600">{showErr('businessRegionProvince')}</p>}
        </div>
      ) : (
        // Foreign: Italian sigle don't apply — keep a free-text region/state field
        <Field className="sm:col-span-2" label={t('billing.provinceRegion')} required value={value.businessRegionProvince ?? ''} onChange={(e) => set('businessRegionProvince', e.target.value)} error={showErr('businessRegionProvince')} />
      )}
      {/* Nazione is a read-only mirror of the contact/anagrafica country (single source above) */}
      <Field className="sm:col-span-6 opacity-70" label={t('billing.country')} value={countryName(value.businessCountry)} readOnly disabled hint={t('billing.countryHint')} />
    </div>
  )

  return (
    <div>
      <div className="mb-4 flex gap-1 rounded-xl border border-line bg-card p-1">
        <Tab id="privato" label={t('billing.typePrivate')} />
        <Tab id="azienda" label={t('billing.typeCompany')} />
      </div>

      {type === 'privato' ? (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Nome/Cognome are read-only mirrors of the contact name above — shown so the user
                sees who the invoice is addressed to, but edited only in one place. */}
            <Field label={t('billing.firstName')} value={cleanName(value.name)} readOnly disabled className="opacity-70" />
            <Field label={t('billing.lastName')} value={value.lastname ?? ''} readOnly disabled className="opacity-70" />
            {/* Codice Fiscale only for Italy — foreign private clients leave it empty (FatturaPA). */}
            {it && (
              <Field className="sm:col-span-2" label={t('billing.taxCode')} required value={value.codiceFiscale ?? ''} onChange={(e) => set('codiceFiscale', e.target.value.toUpperCase())} error={showErr('codiceFiscale')} hint={t('billing.taxCodePrivateHint')} />
            )}
          </div>
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ink-400">{t('billing.residenceAddress')}</p>
            {addressFields}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field className="sm:col-span-2" label={t('billing.companyName')} required value={value.businessName ?? ''} onChange={(e) => set('businessName', e.target.value)} error={showErr('businessName')} autoComplete="organization" />
            {/* Codice Fiscale only for Italy — a foreign company identifies via its P.IVA/VAT. */}
            {it && (
              <Field label={t('billing.taxCode')} required value={value.codiceFiscale ?? ''} onChange={(e) => set('codiceFiscale', e.target.value.toUpperCase())} error={showErr('codiceFiscale')} hint={t('billing.taxCodeHint')} />
            )}
            {/* P.IVA editable for Italy (11 cifre) and EU (real VAT with country prefix). Extra-EU is a
                fixed FatturaPA placeholder (OO99999999999) → hidden, set automatically on the backend. */}
            {(it || eu) && (
              <Field
                className={it ? undefined : 'sm:col-span-2'}
                label={t('billing.vat')}
                required
                value={value.businessVatId ?? ''}
                onChange={(e) => set('businessVatId', e.target.value)}
                error={showErr('businessVatId')}
                hint={it ? t('billing.vatItalyHint') : t('billing.vatEuHint')}
                placeholder={it ? undefined : t('billing.vatEuPlaceholder')}
              />
            )}
            {!it && !eu && (
              <p className="text-xs text-ink-400 sm:col-span-2">{t('billing.vatExtraEu')}</p>
            )}
          </div>

          {addressFields}

          {/* Codice Destinatario (SDI) + PEC are Italy-only. Foreign → SDI auto-set to XXXXXXX by the backend. */}
          {it && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t('billing.sdi')} required value={value.businessSDI ?? ''} onChange={(e) => set('businessSDI', e.target.value.toUpperCase())} error={showErr('businessSDI')} hint={t('billing.sdiHint')} />
              <Field label={t('billing.pec')} type="email" value={value.businessPec ?? ''} onChange={(e) => set('businessPec', e.target.value)} />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('billing.email')} required type="email" value={value.businessEmail ?? ''} onChange={(e) => set('businessEmail', e.target.value)} error={showErr('businessEmail')} />
            <Field label={t('billing.phone')} value={value.phoneNumber ?? ''} onChange={(e) => set('phoneNumber', e.target.value)} />
            <Field className="sm:col-span-2" label={t('billing.contact')} value={value.referente ?? ''} onChange={(e) => set('referente', e.target.value)} />
          </div>
        </div>
      )}
    </div>
  )
}
